import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { supabase, isSupabaseEnabled } from "@/lib/supabase";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

// ─── Admin Password Recovery Flow V1 ───────────────────────────────────────
// Public route the user lands on after clicking the link from
// supabase.auth.resetPasswordForEmail() (see admin.tsx's LoginGate). Supabase
// appends the recovery grant to the URL itself (hash fragment, implicit
// flow) — the client singleton (lib/supabase.ts, detectSessionInUrl: true by
// default) parses it automatically and fires onAuthStateChange with event
// "PASSWORD_RECOVERY" once a session is established from it.
//
// This route deliberately does NOT reuse useAuth()'s session — a normal
// already-signed-in admin just browsing to this URL must not be able to
// change their password without a real recovery grant. It sets up its own
// onAuthStateChange subscription and only unlocks the form on the specific
// "PASSWORD_RECOVERY" event, with a short timeout that falls back to a
// blocking "no valid session" state otherwise (covers: direct navigation,
// an already-consumed link, or a link Supabase already flagged as expired
// via ?error=/#error= params).

type Status = "checking" | "ready" | "expired" | "no-session" | "success";

const RECOVERY_WAIT_MS = 6_000;

function stripUrlFragment() {
  // Defense-in-depth on top of Supabase's own cleanup — the URL may carry
  // access_token/refresh_token (success) or error/error_code (expired
  // link) in the hash; neither should linger in the address bar, browser
  // history, or get copy-pasted/screenshotted by the user.
  if (window.location.hash || window.location.search) {
    window.history.replaceState(null, "", window.location.pathname);
  }
}

function detectExpiredLinkFromUrl(): boolean {
  // Supabase appends #error=access_denied&error_code=otp_expired&... (or
  // similar) to the redirect URL when the recovery link is invalid/expired.
  // Only the error_code is inspected — never the token params — and nothing
  // here is logged.
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash || window.location.search);
  const errorCode = params.get("error_code") || params.get("error");
  return errorCode === "otp_expired" || errorCode === "access_denied";
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>(isSupabaseEnabled ? "checking" : "no-session");

  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError,       setFormError]       = useState<string | null>(null);
  const [submitting,      setSubmitting]      = useState(false);

  const settledRef = useRef(false);

  useEffect(() => {
    if (!supabase) return;

    if (detectExpiredLinkFromUrl()) {
      settledRef.current = true;
      setStatus("expired");
      stripUrlFragment();
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        settledRef.current = true;
        setStatus("ready");
        stripUrlFragment();
      }
    });

    const timer = setTimeout(() => {
      if (!settledRef.current) {
        setStatus("no-session");
        stripUrlFragment();
      }
    }, RECOVERY_WAIT_MS);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status !== "ready" || !supabase) return;

    if (newPassword.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("Las contraseñas no coinciden.");
      return;
    }

    setFormError(null);
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        setFormError("No pudimos actualizar tu contraseña. Probá de nuevo o solicitá un nuevo enlace.");
        return;
      }
      // Never held in state beyond this point, never written to storage.
      setNewPassword("");
      setConfirmPassword("");
      setStatus("success");
      setTimeout(() => { void navigate({ to: "/admin" }); }, 3000);
    } catch {
      setFormError("No pudimos actualizar tu contraseña. Probá de nuevo o solicitá un nuevo enlace.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isSupabaseEnabled || !supabase) {
    return (
      <Shell title="Sift Trip Admin">
        <p className="mt-3 text-sm text-muted-foreground">
          Configuración incompleta — el restablecimiento de contraseña no está disponible en este entorno.
        </p>
      </Shell>
    );
  }

  if (status === "checking") {
    return (
      <Shell title="Restablecer contraseña">
        <p className="mt-3 text-sm text-muted-foreground">Verificando enlace…</p>
      </Shell>
    );
  }

  if (status === "expired") {
    return (
      <Shell title="Restablecer contraseña">
        <p className="mt-3 text-sm text-muted-foreground">
          El enlace expiró. Solicita uno nuevo.
        </p>
      </Shell>
    );
  }

  if (status === "no-session") {
    return (
      <Shell title="Restablecer contraseña">
        <p className="mt-3 text-sm text-muted-foreground">
          Este enlace no es válido o ya fue usado. Solicita uno nuevo desde /admin.
        </p>
      </Shell>
    );
  }

  if (status === "success") {
    return (
      <Shell title="Contraseña actualizada">
        <p className="mt-3 text-sm text-muted-foreground">
          Tu contraseña se actualizó correctamente.
        </p>
        <button
          onClick={() => void navigate({ to: "/admin" })}
          className="mt-6 w-full rounded-lg bg-[oklch(0.580_0.220_256)] px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
        >
          Ir al admin
        </button>
      </Shell>
    );
  }

  // status === "ready"
  return (
    <Shell title="Elegí tu nueva contraseña">
      <form onSubmit={handleSubmit} className="mt-6 space-y-3 text-left">
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Nueva contraseña"
          autoComplete="new-password"
          autoFocus
          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-[oklch(0.580_0.220_256)] focus:ring-1 focus:ring-[oklch(0.580_0.220_256)]"
        />
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirmar contraseña"
          autoComplete="new-password"
          className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-[oklch(0.580_0.220_256)] focus:ring-1 focus:ring-[oklch(0.580_0.220_256)]"
        />
        {formError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-950 dark:text-red-400">
            {formError}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || !newPassword || !confirmPassword}
          className="w-full rounded-lg bg-[oklch(0.580_0.220_256)] px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {submitting ? "Actualizando…" : "Actualizar contraseña"}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <p className="text-2xl font-semibold text-foreground">{title}</p>
        {children}
      </div>
    </div>
  );
}
